import { FC, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../../context/notificationContext";
import useI18n from "../../../../../hooks/useTranslations";
import { HrailSupport } from "../../../../../http/hrail/types/supports";
import { useHrailSupportsApi } from "../../../../../http/hrail/useSupports";
import { SearchAdminItem } from "../../../../../http/types/items";
import Button from "../../../../ui/Button";
import Form from "../../../../ui/Form";
import FormField from "../../../../ui/FormField";
import FormFieldsContainer from "../../../../ui/FormFieldsContainer";
import ItemQuantityFieldArray from "../../../../ui/ItemQuanitityField";
import Numeric from "../../../../ui/Numeric";
import VisibilityScope, { VisibilityScopeValue } from "../../../../ui/VisibilityScope";

interface SupportFormBody {
  order: number | null;
  visibility: VisibilityScopeValue;
  items: {
    item: SearchAdminItem | null;
    quantityPerSupport: number | null;
  }[];
}

interface SupportFormProps {
  support?: HrailSupport;
  isDisabled?: boolean;
  isLoading?: boolean;
  onClose?: () => void;
  onCreated?: (id: string) => void;
  onSaved?: () => void;
}

const toDefaultValues = (support?: HrailSupport): SupportFormBody => ({
  order: support?.order ?? 50,
  visibility: support?.visibility ?? "internal",
  items: support?.items?.map((row) => ({
    item: row.item,
    quantityPerSupport: row.quantityPerSupport,
  })) ?? [{ item: null, quantityPerSupport: null }],
});

const SupportForm: FC<SupportFormProps> = ({
  support,
  isDisabled,
  isLoading,
  onClose,
  onCreated,
  onSaved,
}) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isClosingRef = useRef(false);

  const supportApi = useHrailSupportsApi();

  const supportId = support?.id;

  const defaultValues = useMemo(() => toDefaultValues(support), [support]);

  const closeAfterSave = () => {
    if (onClose) {
      isClosingRef.current = true;
      onClose();
    }
  };

  return (
    <Form<SupportFormBody>
      defaultValues={defaultValues}
      submitText={i18n.translation.common.save}
      disabled={isDisabled || isLoading || isSubmitting}
      secondaryButton={
        onClose ? (
          <Button buttonProps={{ onClick: onClose }} type="secondary">
            {i18n.translation.common.cancel}
          </Button>
        ) : undefined
      }
      onSubmit={() => async (formData) => {
        setIsSubmitting(true);

        const items = formData.items.map((row) => ({
          itemId: row.item?.id ?? "",
          quantityPerSupport: row.quantityPerSupport!,
        }));

        try {
          if (support) {
            await supportApi.updateAdminHrailSupport(support.id, {
              items,
              order: formData.order ?? 50,
              visibility: formData.visibility,
            });

            dispatch(showSuccessNotification(i18n.translation));
            closeAfterSave();
            onSaved?.();
          } else {
            const created = await supportApi.insertAdminHrailSupport({
              items,
              order: formData.order ?? 50,
              visibility: formData.visibility,
            });

            dispatch(showSuccessNotification(i18n.translation));
            onCreated?.(created.id);
            closeAfterSave();
            onSaved?.();
          }
        } catch (err) {
          dispatch(showErrorNotification(i18n, err));
        } finally {
          setIsSubmitting(false);
        }
      }}
      fullWidth
    >
      {({ control, getValues, reset, setValue }) => {
        useEffect(() => {
          if (isSubmitting || isClosingRef.current) return;

          reset(defaultValues);
        }, [reset, supportId]);

        const disabled = isDisabled || isLoading || isSubmitting;

        return (
          <FormFieldsContainer fullWidth>
            <Controller
              name="order"
              control={control}
              rules={{ required: true, min: 0 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.order} error={fieldState.error} isOptional={false}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={typeof field.value === "number" ? field.value : null}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      min={0}
                      disabled={disabled}
                      onBlur={(value) =>
                        setValue("order", value, {
                          shouldValidate: true,
                        })
                      }
                    />
                  )}
                </FormField>
              )}
            />

            <Controller
              name="visibility"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.visibility} error={fieldState.error}>
                  {({ labelId }) => (
                    <VisibilityScope
                      {...field}
                      inputId={labelId}
                      isDisabled={disabled}
                      onChange={(value) => setValue("visibility", value)}
                    />
                  )}
                </FormField>
              )}
            />

            <ItemQuantityFieldArray
              control={control}
              name="items"
              getValues={getValues}
              disabled={disabled}
              itemLabelText={i18n.translation.items.singular}
              quantityLabelText={i18n.translation.common.quantityPerSupport}
              addButtonTitle={i18n.translation.common.add}
              deleteButtonTitle={i18n.translation.common.delete}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default SupportForm;
